import {
  useInScopeModel,
  useStatefullyAwareInScopeModel,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { BaseModel } from '@cotera/client/v0/actions/framework/model.base';
import type { ModelResource } from '@cotera/client/v0/actions/framework/types';
import {
  PersistentWatchable,
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework/watchable';
import {
  EventWatchable,
  type ReadonlyEventWatchable,
} from '@cotera/client/v0/actions/framework/watchable';
import {
  BalanceInfoSchema,
  BroadcastEventType,
  ResourceType,
  type BroadcastEventPayloadMap,
} from '@cotera/api';
import type {
  CoteraApiV2,
  ResourceFolderItemsOutput,
  TenantedClient,
  ToolListItem,
} from '@cotera/client/app/etc/api/typed-client';
import type { StorageConnection } from '@cotera/client/v0/models/user-branches';
import { Assert } from '@cotera/utilities';
import z from 'zod/v3';
import { DEFAULT_PERSONALITY, Personality } from '../etc/personality';
import {
  matchesSidebarUnfiledSection,
  type SidebarFolderTreeItem,
} from './sidebar/coco-sidebar-folder-tree';
import {
  WatchablesFactory,
  type CocoMentionResourcesData,
} from '../watchables/watchables-factory';
import {
  DEFAULT_UI_PREFERENCES,
  UIPreferences,
  UI_PREFERENCES_STORAGE_KEY,
} from '../settings/ui-preferences';
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  queryKeys,
  RESOURCE_LIST_FETCH_STALE_MS,
} from '@cotera/client/app/hooks/query-cache-keys';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';

type BalanceInfo = z.infer<typeof BalanceInfoSchema>;

/** Item used by agents list: id, title, and optional description */
export type AgentListItem = {
  id: string;
  ownerId: string;
  title: string | null;
  description?: string | null;
  folderId?: string | null;
  folderName?: string | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  emoji?: string | null;
};

type Props = {
  orgId: string;
  client: TenantedClient;
  userId: string;
  untenantedClient: CoteraApiV2['u'];
  watchablesFactory: Pick<
    WatchablesFactory,
    | 'makeCreditBalanceUpdatedWatchable'
    | 'makeCocoMentionResourcesWatchable'
    | 'makeRecentChatsListWatchable'
    | 'makeResourceUpdatedWatchable'
    | 'makeToolsListWatchable'
  >;
  storage: StorageConnection;
};

/** Item used by the sidebar: id, title (derived), and optional last_user_message */
export type RecentChatListItem = {
  id: string;
  actor_id: string;
  title: string;
  last_user_message: unknown;
  hasUnreadAssistant: boolean;
};

export type FolderScope = 'private' | 'shared';

/** Sidebar folder list per scope (private/shared); refetches on ResourceUpdated (resourceType folder) */
export type SidebarFolderListData = {
  items: Array<{
    id: string;
    name: string;
    createdAt: Date;
    updatedAt: Date;
    scope: 'user' | 'org' | 'external';
    childrenCount: number;
  }>;
  total: number;
} | null;
export type FolderContentsData = ResourceFolderItemsOutput | null;

/** Root sidebar: unfiled chat count + agent rows for a Private/Shared section. */
export type SidebarRootUnfiledData = {
  chatCount: number;
  agents: Extract<SidebarFolderTreeItem, { type: 'agent' }>[];
} | null;

function extractCreditBalanceFromBalanceInfo(
  balanceInfo: BalanceInfo | null
): number {
  if (balanceInfo === null) {
    return 0;
  }
  if (balanceInfo.kind === 'orb-paygo-business') {
    return balanceInfo.currentCreditBalance / 0.005;
  }
  if (balanceInfo.kind === 'orb-enterprise') {
    const cc = balanceInfo.balance.creditsCredits;
    if (cc.kind === 'positive') {
      return cc.credits;
    }
    if (cc.kind === 'negative') {
      return -cc.creditsOverageAmount;
    }
  }
  return 0;
}

const SIDEBAR_FOLDER_LIST_LIMIT = 5;
const SIDEBAR_FOLDER_CONTENTS_PREVIEW_LIMIT = 40;
const MAX_FOLDER_CONTENTS_WATCHABLES = 300;
const RESOURCE_LIST_EVENT_DEBOUNCE_MS = 500 as const;

const EMPTY_COCO_MENTION_RESOURCES: CocoMentionResourcesData = {
  agents: [],
  documents: [],
  folders: [],
  datasets: [],
  workflows: [],
};

function makeFolderListWatchableKey(scope: FolderScope, limit: number): string {
  return `${scope}:${limit}`;
}

function makeFolderContentsWatchableKey(params: {
  folderId: string;
  folderScope?: FolderScope;
  itemTypes?: ResourceType[];
  limit: number;
  offset: number;
}): string {
  return [
    params.folderId,
    params.folderScope ?? 'any',
    params.itemTypes?.join(',') ?? 'all',
    String(params.limit),
    String(params.offset),
  ].join('|');
}

export class AppModel extends BaseModel {
  private readonly client: TenantedClient;
  private readonly watchablesFactory: Pick<
    WatchablesFactory,
    | 'makeCreditBalanceUpdatedWatchable'
    | 'makeCocoMentionResourcesWatchable'
    | 'makeRecentChatsListWatchable'
    | 'makeResourceUpdatedWatchable'
    | 'makeToolsListWatchable'
  >;
  private readonly balanceInfoEventWatchable: ReadonlyEventWatchable<BalanceInfo | null>;

  readonly creditBalance: ReadonlyWatchable<number>;
  readonly billingKind: ReadonlyWatchable<string | null>;

  private readonly recentChatsEventWatchable: ReadonlyEventWatchable<
    RecentChatListItem[] | null
  >;

  readonly recentChats: ReadonlyWatchable<RecentChatListItem[]>;
  readonly recentChatsStatus: ReadonlyWatchable<'loading' | 'ready'>;

  private readonly toolsEventWatchable: ReadonlyEventWatchable<
    ToolListItem[] | null
  >;

  /** Org's tool list, kept fresh by tool-credential-updated events. */
  readonly tools: ReadonlyWatchable<ToolListItem[]>;
  readonly toolsStatus: ReadonlyWatchable<'loading' | 'ready'>;

  private readonly cocoMentionResourcesEventWatchable: ReadonlyEventWatchable<CocoMentionResourcesData | null>;

  /** Agents, documents, folders, and datasets for @-mention menus. */
  readonly cocoMentionResources: ReadonlyWatchable<CocoMentionResourcesData>;
  readonly cocoMentionResourcesStatus: ReadonlyWatchable<'loading' | 'ready'>;

  readonly personality: Watchable<Personality> =
    Watchable.fromValue(DEFAULT_PERSONALITY);

  readonly uiPreferences: PersistentWatchable<UIPreferences>;

  private readonly cliConnectionStatusEventWatchable: ReturnType<
    typeof EventWatchable.for<
      | BroadcastEventPayloadMap[typeof BroadcastEventType.CliConnectionStatusChanged]
      | null,
      { userId: string }
    >
  >;

  readonly cliConnectionStatus: ReadonlyWatchable<
    | BroadcastEventPayloadMap[typeof BroadcastEventType.CliConnectionStatusChanged]
    | null
  >;

  private readonly folderListWatchablesByKey = new Map<
    string,
    ReadonlyEventWatchable<SidebarFolderListData>
  >();
  private readonly folderContentsWatchablesByKey = new Map<
    string,
    ReadonlyEventWatchable<FolderContentsData>
  >();
  private readonly sidebarRootUnfiledWatchablesByKey = new Map<
    string,
    ReadonlyEventWatchable<SidebarRootUnfiledData>
  >();

  constructor({
    orgId,
    userId,
    client,
    untenantedClient,
    watchablesFactory,
    storage,
  }: Props) {
    super('coco');
    this.client = client;
    this.watchablesFactory = watchablesFactory;

    this.uiPreferences = PersistentWatchable.fromStorage<UIPreferences>({
      storage,
      storageKey: UI_PREFERENCES_STORAGE_KEY,
      defaultValue: DEFAULT_UI_PREFERENCES,
      map: (value) => ({ ...DEFAULT_UI_PREFERENCES, ...value }),
      equalityFn: (a, b) => a.showCredits === b.showCredits,
    });
    this.balanceInfoEventWatchable =
      this.watchablesFactory.makeCreditBalanceUpdatedWatchable<
        BalanceInfo | null,
        { orgId: string }
      >({
        runOnMount: true,
        initialValue: null,
        fn: async (_payload, _get) => {
          const res = await client.billingSettings.balanceInfo({});
          if (res.isErr()) {
            return null;
          }
          return res.value.balanceInfo;
        },
        filter: (payload) => payload?.orgId === orgId,
      });

    this.creditBalance = Watchable.from((get) => {
      return extractCreditBalanceFromBalanceInfo(
        get(this.balanceInfoEventWatchable)
      );
    });

    this.billingKind = Watchable.from((get) => {
      return get(this.balanceInfoEventWatchable)?.kind ?? null;
    });

    this.recentChatsEventWatchable =
      this.watchablesFactory.makeRecentChatsListWatchable({
        client,
      });

    this.recentChats = Watchable.from((get) => {
      const raw = get(this.recentChatsEventWatchable);
      return raw ?? [];
    });
    this.recentChatsStatus = Watchable.from((get) => {
      const raw = get(this.recentChatsEventWatchable);
      return raw === null ? 'loading' : 'ready';
    });

    this.toolsEventWatchable = this.watchablesFactory.makeToolsListWatchable({
      orgId,
      client,
    });
    this.tools = Watchable.from((get) => get(this.toolsEventWatchable) ?? []);
    this.toolsStatus = Watchable.from((get) =>
      get(this.toolsEventWatchable) === null ? 'loading' : 'ready'
    );

    this.cocoMentionResourcesEventWatchable =
      this.watchablesFactory.makeCocoMentionResourcesWatchable({
        orgId,
        client,
      });
    this.cocoMentionResources = Watchable.from(
      (get) =>
        get(this.cocoMentionResourcesEventWatchable) ??
        EMPTY_COCO_MENTION_RESOURCES
    );
    this.cocoMentionResourcesStatus = Watchable.from((get) =>
      get(this.cocoMentionResourcesEventWatchable) === null
        ? 'loading'
        : 'ready'
    );

    // CLI connection status watchable
    this.cliConnectionStatusEventWatchable = EventWatchable.for<
      | BroadcastEventPayloadMap[typeof BroadcastEventType.CliConnectionStatusChanged]
      | null,
      { userId: string }
    >({
      event: BroadcastEventType.CliConnectionStatusChanged,
      runOnMount: true,
      initialValue: null,
      fn: async () => {
        const result = await untenantedClient.cli.getStatus();
        if (result.isErr()) {
          return null;
        }
        return {
          userId: result.value.userId,
          connected: result.value.connected,
          timestamp: new Date().toISOString(),
        };
      },
      filter: (payload) => payload?.userId === userId,
    });

    this.cliConnectionStatus = Watchable.from((get) => {
      return get(this.cliConnectionStatusEventWatchable);
    });

    // Warm commonly used sidebar list watchables.
    void this.getFolderListWatchable('private');
    void this.getFolderListWatchable('shared');
    // Not warming getSidebarRootUnfiledWatchable: nothing renders it, and its chat
    // count materializes every accessible chat to read `total` off a 1-row page.
  }

  /**
   * Unfiled resources for a sidebar scope: chat count and a small list of agents
   * for leaf rows. Refetches when chats, agents, or folders change placement.
   */
  getSidebarRootUnfiledWatchable(
    scope: FolderScope
  ): ReadonlyEventWatchable<SidebarRootUnfiledData> {
    const key = `sidebar-root-unfiled:${scope}`;
    const existing = this.sidebarRootUnfiledWatchablesByKey.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const watchable = this.watchablesFactory.makeResourceUpdatedWatchable<
      SidebarRootUnfiledData,
      { resourceType?: string }
    >({
      debounceMs: RESOURCE_LIST_EVENT_DEBOUNCE_MS,
      runOnMount: true,
      initialValue: null,
      filter: (payload) =>
        payload?.resourceType === 'chat' ||
        payload?.resourceType === 'agent' ||
        payload?.resourceType === 'folder',
      fn: async () => {
        const AGENT_SCAN = 100;
        // Chats honour the server-side scope filter: one page (`limit: 1`, the
        // v20260601 schema min) and read `total` — correct past 100 and one page
        // instead of scanning 100 rows and counting in JS. Agents still scan +
        // filter until they honour the same params.
        const chatRes = await this.client.resource.list({
          type: ResourceType.Chat,
          limit: 1,
          offset: 0,
          unfiledOnly: true,
          folderScope: scope,
        });
        const agentRes = await this.client.resource.list({
          type: ResourceType.Agent,
          limit: AGENT_SCAN,
          offset: 0,
        });
        const chatsOut = Assert.assertOk(chatRes);
        const agentsOut = Assert.assertOk(agentRes);
        if (chatsOut.type !== 'chat') {
          return { chatCount: 0, agents: [] };
        }
        const chatCount = chatsOut.total;
        if (agentsOut.type !== 'agent') {
          return {
            chatCount,
            agents: [],
          };
        }
        const agentItems = agentsOut.items
          .filter((a) =>
            matchesSidebarUnfiledSection(
              a.folderId ?? null,
              a.folderScope ?? null,
              scope
            )
          )
          .slice(0, SIDEBAR_FOLDER_LIST_LIMIT);
        const agents: Extract<SidebarFolderTreeItem, { type: 'agent' }>[] =
          agentItems.map((a) => ({
            type: ResourceType.Agent,
            data: {
              id: a.id,
              orgId: a.orgId,
              ownerId: a.ownerId,
              agentKey: a.agentKey,
              model: a.model,
              description: a.description,
              type: a.type,
              status: a.status,
              createdAt: a.createdAt,
              updatedAt: a.updatedAt,
              title: a.title ?? null,
              folderId: a.folderId ?? null,
              folderName: a.folderName ?? null,
              folderScope: a.folderScope ?? null,
            },
          }));
        return { chatCount, agents };
      },
    });
    this.sidebarRootUnfiledWatchablesByKey.set(key, watchable);
    return watchable;
  }

  getFolderListWatchable(
    scope: FolderScope,
    limit = SIDEBAR_FOLDER_LIST_LIMIT
  ): ReadonlyEventWatchable<SidebarFolderListData> {
    const key = makeFolderListWatchableKey(scope, limit);
    const existing = this.folderListWatchablesByKey.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const watchable = this.watchablesFactory.makeResourceUpdatedWatchable<
      SidebarFolderListData,
      { resourceType?: string }
    >({
      debounceMs: RESOURCE_LIST_EVENT_DEBOUNCE_MS,
      runOnMount: true,
      initialValue: null,
      filter: (payload) => payload?.resourceType === 'folder',
      fn: async () => {
        const res = await this.client.resource.list({
          type: ResourceType.Folder,
          folderScope: scope,
          limit,
        });
        const out = Assert.assertOk(res);
        if (out.type !== 'folder') {
          return { items: [], total: 0 };
        }
        const items = [...out.items].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        return {
          items: items.map((f) => ({
            id: f.id,
            name: f.name,
            createdAt: f.createdAt,
            updatedAt: f.updatedAt,
            scope: f.scope,
            childrenCount: f.childrenCount ?? 0,
          })),
          total: out.total,
        };
      },
    });
    this.folderListWatchablesByKey.set(key, watchable);
    return watchable;
  }

  getFolderContentsWatchable(params: {
    folderId: string;
    folderScope?: FolderScope;
    itemTypes?: ResourceType[];
    limit?: number;
    offset?: number;
  }): ReadonlyEventWatchable<FolderContentsData> {
    const limit = params.limit ?? SIDEBAR_FOLDER_CONTENTS_PREVIEW_LIMIT;
    const offset = params.offset ?? 0;
    const key = makeFolderContentsWatchableKey({
      folderId: params.folderId,
      folderScope: params.folderScope,
      itemTypes: params.itemTypes,
      limit,
      offset,
    });
    const existing = this.folderContentsWatchablesByKey.get(key);
    if (existing !== undefined) {
      return existing;
    }
    if (
      this.folderContentsWatchablesByKey.size >= MAX_FOLDER_CONTENTS_WATCHABLES
    ) {
      const oldestEntry = this.folderContentsWatchablesByKey.entries().next();
      if (oldestEntry.done === false) {
        const [oldestKey, oldestWatchable] = oldestEntry.value;
        oldestWatchable.unsubscribe();
        this.folderContentsWatchablesByKey.delete(oldestKey);
      }
    }
    const watchable = this.watchablesFactory.makeResourceUpdatedWatchable<
      FolderContentsData,
      { resourceType?: string; resourceId?: string }
    >({
      runOnMount: false,
      initialValue: null,
      filter: (payload) =>
        payload?.resourceType === 'folder' &&
        payload?.resourceId === params.folderId,
      fn: async () => {
        const res = await this.client.resource.folder.getFolderItems({
          folderId: params.folderId,
          ...(params.folderScope !== undefined
            ? { folderScope: params.folderScope }
            : {}),
          ...(params.itemTypes !== undefined && params.itemTypes.length > 0
            ? { itemTypes: params.itemTypes }
            : {}),
          sort: 'position',
          sortDir: 'asc',
          limit,
          offset,
        });
        return Assert.assertOk(res);
      },
    });
    this.folderContentsWatchablesByKey.set(key, watchable);
    return watchable;
  }

  getFolderContentsPreviewWatchable(params: {
    folderId: string;
    scope: FolderScope;
  }): ReadonlyEventWatchable<FolderContentsData> {
    return this.getFolderContentsWatchable({
      folderId: params.folderId,
      folderScope: params.scope,
      limit: SIDEBAR_FOLDER_CONTENTS_PREVIEW_LIMIT,
      offset: 0,
    });
  }

  resources(): ModelResource[] {
    // Named as resources rather than routed. These used to carry hardcoded
    // paths, which sent agents to `/agent/:id` — a legacy redirect route, not a
    // page — so picking one from search went nowhere.
    //
    // Each row carries the icon its type wears in the Library, so a search
    // result is recognisable as the same thing. A row with no icon renders as
    // bare text, which in a flat list reads as a section heading.
    const chats: ModelResource[] = this.recentChats.snapshot().map((chat) => ({
      t: 'resource',
      resourceType: ResourceType.Chat,
      resourceId: chat.id,
      name: chat.title,
      group: 'Chat',
      icon: 'chat',
      priority: 5,
    }));
    // Also surface Library resources in global Search (previously chats-only).
    const mentions = this.cocoMentionResources.snapshot();
    const agents: ModelResource[] = mentions.agents.map((a) => ({
      t: 'resource',
      resourceType: ResourceType.Agent,
      resourceId: a.id,
      name: a.title ?? a.agentKey ?? 'Untitled agent',
      group: 'Agent',
      icon: 'agent',
      priority: 4,
    }));
    const documents: ModelResource[] = mentions.documents.map((d) => ({
      t: 'resource',
      resourceType: ResourceType.Document,
      resourceId: d.id,
      name: d.title ?? 'Untitled document',
      group: 'Document',
      icon: 'file-text',
      priority: 4,
    }));
    const folders: ModelResource[] = mentions.folders.map((f) => ({
      t: 'resource',
      resourceType: ResourceType.Folder,
      resourceId: f.id,
      name: f.name,
      group: 'Folder',
      icon: 'folder',
      priority: 4,
    }));
    return [...chats, ...agents, ...documents, ...folders];
  }

  dispose() {
    this.balanceInfoEventWatchable.unsubscribe();
    this.recentChatsEventWatchable.unsubscribe();
    this.toolsEventWatchable.unsubscribe();
    this.cocoMentionResourcesEventWatchable.unsubscribe();
    this.cliConnectionStatusEventWatchable.unsubscribe();
    for (const watchable of this.folderListWatchablesByKey.values()) {
      watchable.unsubscribe();
    }
    for (const watchable of this.folderContentsWatchablesByKey.values()) {
      watchable.unsubscribe();
    }
    for (const watchable of this.sidebarRootUnfiledWatchablesByKey.values()) {
      watchable.unsubscribe();
    }
  }
}

export const useAppModel = () => {
  return useInScopeModel(AppModel);
};

// Stable fallback watchables used when no AppModel is in scope — a subtree
// mounted outside the app shell, or a test harness. Never read for their value
// in that case — the fallback react-query below is the source of truth — but
// useWatchableValue requires a non-null watchable, so we hand it these.
const EMPTY_COCO_MENTION_RESOURCES_WATCHABLE =
  Watchable.fromValue<CocoMentionResourcesData>(EMPTY_COCO_MENTION_RESOURCES);
const READY_COCO_MENTION_STATUS_WATCHABLE = Watchable.fromValue<
  'loading' | 'ready'
>('ready');

// Resource types backing the @-mention menus, in the order their slices appear
// on CocoMentionResourcesData.
const COCO_MENTION_RESOURCE_TYPES = [
  ResourceType.Agent,
  ResourceType.Document,
  ResourceType.Folder,
  ResourceType.Dataset,
  ResourceType.Workflow,
] as const;

const COCO_MENTION_LIST_LIMIT = 100;

/**
 * Coco @-mention resource lists (agents, documents, folders, datasets).
 * Prefers the shared watchable held on {@link AppModel}, which fetches once and
 * refreshes on matching `resource-updated` events.
 *
 * When no AppModel is in scope — a subtree mounted outside the app shell, or a
 * test harness — it falls back to direct react-queries so consumers like
 * useCocoMentions still resolve mentions instead of crashing. Those queries share
 * the same `resource.list` cache keys the AppModel watchable invalidates, so they
 * stay live even in the fallback path.
 */
export const useCocoMentionResources = (): {
  resources: CocoMentionResourcesData;
  isLoading: boolean;
} => {
  const appModel = useStatefullyAwareInScopeModel(AppModel);
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();

  // Only fetch when AppModel is absent; otherwise disabled and the watchables
  // below drive the value.
  const fallbackQueries = useQueries({
    queries: COCO_MENTION_RESOURCE_TYPES.map((type) => ({
      queryKey: queryKeys.resource.list({
        orgId,
        type,
        limit: COCO_MENTION_LIST_LIMIT,
      }),
      staleTime: RESOURCE_LIST_FETCH_STALE_MS,
      enabled: appModel === null,
      queryFn: async () =>
        Assert.assertOk(
          await client.resource.list({ type, limit: COCO_MENTION_LIST_LIMIT })
        ),
    })),
  });

  const agentData = fallbackQueries[0]?.data;
  const documentData = fallbackQueries[1]?.data;
  const folderData = fallbackQueries[2]?.data;
  const datasetData = fallbackQueries[3]?.data;
  const workflowData = fallbackQueries[4]?.data;

  const fallbackResources = useMemo<CocoMentionResourcesData>(
    () => ({
      agents: agentData?.type === 'agent' ? agentData.items : [],
      documents: documentData?.type === 'document' ? documentData.items : [],
      folders: folderData?.type === 'folder' ? folderData.items : [],
      datasets: datasetData?.type === 'dataset' ? datasetData.items : [],
      workflows: workflowData?.type === 'workflow' ? workflowData.items : [],
    }),
    [agentData, documentData, folderData, datasetData, workflowData]
  );

  const watchableResources = useWatchableValue(
    appModel?.cocoMentionResources ?? EMPTY_COCO_MENTION_RESOURCES_WATCHABLE
  );
  const watchableStatus = useWatchableValue(
    appModel?.cocoMentionResourcesStatus ?? READY_COCO_MENTION_STATUS_WATCHABLE
  );

  if (appModel !== null) {
    return {
      resources: watchableResources,
      isLoading: watchableStatus === 'loading',
    };
  }

  return {
    resources: fallbackResources,
    isLoading: fallbackQueries.some((q) => q.isLoading),
  };
};
